import { MissionEnumsLists, TaskEnums } from "../../../../../common/constants";
import { GeoJsonPoint } from "../../../../../common/maphelpers";
import { GetTaskDriverState, GetTaskStateBackgroundColor } from "../../../../../common/mission";
import Route from "../../../../../common/Route";
import MapPlugin from "../MapPlugin";
import { Checkbox, FormControlLabel, FormGroup, FormLabel, Stack } from "@mui/material";
import LayerSettings from "../../common/LayerSettings";
import { useCallback, useEffect, useState } from "react";
import { useOperationsMap } from "../../OperationsMapProvider";
import { useTranslation } from "react-i18next";

export default class DispatchLayer extends MapPlugin {

  constructor(map) {
    super("dispatch", map);

    this.defaultSettings = {
      showTags: true,
      showCause: true,
      showMissionType: false
    };

    this.styles = {
      td: "vertical-align: top; padding-right: 8px;",
      divider: `border: none; height: 1px; background: ${this.theme.palette.divider}; margin-bottom: 12px;`
    };

    this.addImage("mission_green", { color: true });
    this.addImage("mission_red", { color: true });
    this.addImage("mission_gray", { color: true });
    this.addImage("mission_orange", { color: true });
    this.addImage("mission_blue", { color: true });

    this.on("click", "mission-locations-widget", (ev) => {
      const { properties } = ev.features[0];
      this.emitToProvider("dispatchLayerClick", properties.id);
    });

    if (!this.mobile) {

      this.addPopup(
        "mission-popup",
        "mission-locations-widget",
        (_, properties) => this.renderPropertiesToPopup(properties)
      );

      this.addPopup(
        "mission-popup-cluster",
        "mission-clusters",
        (e, _, callback) => {

          const clusterId = e.features[0].properties.cluster_id;
          const point_count = e.features[0].properties.point_count;
          const clusterSource = this._map.getSource("mission-locations");

          clusterSource.getClusterLeaves(clusterId, point_count, 0, (err, aFeatures) => {
            if (err) return;

            callback(
              aFeatures
                .slice(0, 25)
                .map(f => this.renderPropertiesToPopup(f.properties))
                .join(`<hr style="${this.styles.divider}"/>`)
            );
          })
        },
        { contentInCallback: true }
      );

      this.on("mouseenter", "mission-locations-widget", (ev) => {
        const { properties } = ev.features[0];
        this.setHighlightLowContrast(properties.id);
        this.emitToProvider("dispatchLayerHover", properties.id);
      });

      this.on("mouseleave", "mission-locations-widget", (ev) => {
        this.setHighlightLowContrast();
        this.emitToProvider("dispatchLayerHover", null);
      });
    }
  }

  setHighlightLowContrast(taskId = "none") {
    this._map.setFilter("mission-locations-highlight-low-contrast", ["==", ["get", "id"], taskId]);
  }

  setHighlightHighContrast(taskId = "none") {
    this._map.setFilter("mission-locations-highlight-high-contrast", ["==", ["get", "id"], taskId]);
  }

  onMount() {
    const theme = this.theme;

    this._map.addSource("mission-locations", {
      type: "geojson",
      data: Route.GeoJson(
        this._ops.filteredTasks
          .filter(t => this.isValidTask(t))
          .map(t => this.convertToFeature(t))
      ),
      cluster: true,
      clusterRadius: 35
    });


    this._map.addLayer({
      id: "mission-locations-highlight-low-contrast",
      source: "mission-locations",
      slot: "elevation-3",
      type: "circle",
      filter: ["==", ["get", "id"], "none"],
      paint: {
        "circle-radius": 9,
        "circle-color": "rgba(0,0,0,0.125)",
        "circle-pitch-alignment": "map",
      }
    });

    this._map.addLayer({
      id: "mission-locations-highlight-high-contrast",
      source: "mission-locations",
      slot: "elevation-3",
      type: "circle",
      filter: ["==", ["get", "id"], "none"],
      paint: {
        "circle-radius": 9,
        "circle-color": "rgba(0,0,0,0.125)",
        "circle-stroke-width": 1.5,
        "circle-stroke-color": theme.palette.info.main,
        "circle-pitch-alignment": "map",
      }
    });


    this._map.addLayer({
      id: "mission-locations-widget",
      type: "symbol",
      slot: "elevation-3",
      source: "mission-locations",
      filter: ["!", ["has", "point_count"]],
      layout: {
        "icon-image": ["get", "urgencyIcon"],
        "icon-size": 8,
        "icon-allow-overlap": true,
        "icon-anchor": "bottom",

        "text-allow-overlap": true,
        "text-ignore-placement": true,
        "text-field": ["get", "title"],
        "text-font": [
          "Open Sans Semibold",
          "Arial Unicode MS Bold"
        ],
        "text-size": 12,

        "text-anchor": "top", // Top of the text sits on the coordinate (hanging down)
        "text-justify": "center", // Forces variable-length text to stay perfectly centered
        "text-offset": [0, 0.15]
      },
      paint: {
        "icon-translate": [0, 0],
        "icon-translate-anchor": "viewport",
        "text-halo-color": "#fff",
        "text-halo-width": 2
      }
    });

    this._map.addLayer({
      id: "mission-clusters",
      source: "mission-locations",
      slot: "elevation-3",
      type: "circle",
      filter: ["has", "point_count"],
      paint: {
        "circle-stroke-width": 2,
        "circle-stroke-color": "#000",
        "circle-color": [
          "step",
          ["get", "point_count"],
          theme.palette.info.light,
          4,
          theme.palette.info.main,
          9,
          theme.palette.info.dark,
        ],
        "circle-radius": [
          "step",
          ["get", "point_count"],
          14,
          100,
          20,
          750,
          30
        ]
      }
    });

    this._map.addLayer({
      id: "mission-clusters-text",
      source: "mission-locations",
      slot: "elevation-3",
      type: "symbol",
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["get", "point_count_abbreviated"],
        "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
        "text-size": 12,
      }
    });


  }

  onUnMount() {

    // remove layers
    this._map.removeLayer("mission-clusters");
    this._map.removeLayer("mission-clusters-text");
    this._map.removeLayer("mission-locations-widget");
    this._map.removeLayer("mission-locations-highlight-low-contrast");
    this._map.removeLayer("mission-locations-highlight-high-contrast");

    // remove source
    this._map.removeSource("mission-locations");
  }

  onUpdateDispatchData(filteredTasks, completedTasks) {
    this._map?.getSource("mission-locations")?.setData(
      Route.GeoJson(
        filteredTasks
          .filter(t => this.isValidTask(t))
          .map(t => this.convertToFeature(t))
      )
    );
  }

  isValidTask(task) {
    const location = task?.route?.waypoints[0];
    return !!task && location?.coordinates?.length >= 2;
  }

  convertToFeature(task) {

    const { showTags, showCause, showMissionType } = this.settings;
    const location = task.route.waypoints[0];
    const state = task.state;
    const driverAssignedState = task.execution.driverAssignedState;
    const details = task.missionDetails;

    const cause = showCause ? details?.cause?.name || "" : "";
    const tags = showTags ? details?.tags?.map(t => `[${t.name}]`)?.join("\n") : "";
    const missionType = showMissionType ? this.t(`mission:enums:type:${MissionEnumsLists.Type[details?.type || 0]}`) : ""

    return GeoJsonPoint(
      location.coordinates,
      {
        id: task.id,
        title: [missionType, cause, tags].filter(t => !!t).join("\n").trim(),
        missionId: task.missionId,
        missionNumber: task.missionNumber,
        taskNumber: task.number,
        taskColor: GetTaskStateBackgroundColor(state, driverAssignedState),
        driverName: task.execution?.driver?.name,
        driverAssignedState,
        target: task.missionTarget,
        details: task.missionDetails,
        urgencyIcon: this.getUrgencyIcon(task.urgency),
        state
      }
    );
  }

  getUrgencyIcon(urgency) {
    switch (urgency) {
      case TaskEnums.EstimatedUrgency.None: return "mission_gray";
      case TaskEnums.EstimatedUrgency.Low: return "mission_blue";
      case TaskEnums.EstimatedUrgency.Normal: return "mission_green";
      case TaskEnums.EstimatedUrgency.High: return "mission_orange";
      case TaskEnums.EstimatedUrgency.Critical: return "mission_red";
      default:
        return "mission_gray"
    }
  }


  renderPropertiesToPopup(props) {
    const t = this.t;
    const theme = this.theme;

    // fetch target
    const target = typeof props.target === "string" ? JSON.parse(props.target) : props.target;
    const targetType = target.type;
    const targetDetails = targetType === 1 ? target.carDetails : (targetType === 2 ? target.boatDetails : null);
    const targetDescription = `${!!targetDetails ? `${targetDetails.make || "-"} ${targetDetails.model || "-"}, ${targetDetails.registration || "-"} ` : ""}`;

    // fetch description
    const details = typeof props.details === "string" ? JSON.parse(props.details) : props.details;
    const jobType = details.type === 0 ? "" : t(`mission:enums:type:${MissionEnumsLists.Type[details.type || 0]}`);
    const targetTypeName = target.type === 0 ? "" : t(`mission:enums:targetType:${MissionEnumsLists.TargetType[target?.type || 0]}`);
    const missionDescription = `${jobType} ${targetTypeName}`;

    return (
      `
      <strong>#${props.missionNumber}.${props.taskNumber} ${missionDescription}</strong>
      <table>
        <tr>
          <td style="${this.styles.td}">${t("home:cards:map:target")}</td><td style="color: ${theme.palette.text.secondary}; ${this.styles.td}">${targetDescription}</td>
        </tr>
        <tr>
          <td style="${this.styles.td}">${t("home:cards:map:driver")}</td><td style="color: ${theme.palette.text.secondary}; ${this.styles.td}">${props.driverName || "-"}</td>
        </tr>
        <tr>
          <td style="${this.styles.td}">${t("home:cards:map:state")}</td><td style="color: ${theme.palette.text.secondary}; ${this.styles.td}">${GetTaskDriverState(props.driverAssignedState, props.state, t)}</td>
        </tr>
      </table>
      `
    );
  }

  onUpdateSettings(settings, update) {
    this.updateDispatchData(this._ops.filteredTasks)
  }
}



export function DispatchLayerSettings() {
  const { t } = useTranslation();

  const {
    operationsMap,
    showTags,
    setShowTags,
    showCause,
    setShowCause,
    showMissionType,
    setShowMissionType,
  } = useOperationsMap();

  const [active, setActive] = useState(operationsMap.isPluginActive("dispatch"));

  useEffect(() => {
    const fn = () => {
      setActive(operationsMap.isPluginActive("dispatch"));
    };

    operationsMap.on("reset", fn);
    return () => operationsMap.off("reset", fn);
  }, [operationsMap]);

  // Handle plugin active state
  const onHandleAccordion = useCallback((_, val) => { // TODO handle this better, mount and unmount based on accordion open
    setActive(val);

    if (val) {
      operationsMap.activatePlugin("dispatch");
    } else {
      operationsMap.deactivatePlugin("dispatch");
    }
  }, [operationsMap]);


  const onHandleShowTags = useCallback((ev) => {
    const val = ev.target.checked;
    setShowTags(val);
    operationsMap.updateSettings("dispatch", { showTags: val });
  }, [operationsMap]);

  const onHandleShowCause = useCallback((ev) => {
    const val = ev.target.checked;
    setShowCause(val);
    operationsMap.updateSettings("dispatch", { showCause: val });
  }, [operationsMap]);

  const onHandleShowMissionType = useCallback((ev) => {
    const val = ev.target.checked;
    setShowMissionType(val);
    operationsMap.updateSettings("dispatch", { showMissionType: val });
  }, [operationsMap]);

  return (
    <LayerSettings title={t("operationsMap:layers:dispatch:title")} open={active} onChange={onHandleAccordion}>
      <Stack spacing={1}>

        <FormGroup>
          <FormLabel>{t("operationsMap:layers:dispatch:showLabels")}</FormLabel>
          <FormControlLabel control={<Checkbox checked={showTags} onChange={onHandleShowTags} />} label={t("operationsMap:layers:dispatch:showTags")} />
          <FormControlLabel control={<Checkbox checked={showCause} onChange={onHandleShowCause} />} label={t("operationsMap:layers:dispatch:showCause")} />
          <FormControlLabel control={<Checkbox checked={showMissionType} onChange={onHandleShowMissionType} />} label={t("operationsMap:layers:dispatch:showMissionType")} />
        </FormGroup>


      </Stack>
    </LayerSettings>
  );
}



